import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
// import { DataFrame, GeoDataFrame, Dataset } from '@oceanum/datamesh-js-core';
import { Dataset } from '@oceanum/datamesh';
import { bboxClip, bboxPolygon, pointsWithinPolygon } from '@turf/turf';

// Import the shared dataLoading singleton (defined in data-provider). The
// original code did `const dataLoading = useDataLoading()` at module scope,
// which crashed with a TDZ error ("Cannot access 'useDataLoading' before
// initialization") when the bundler evaluated data-model before data-provider
// finished (data-provider imports DataWrapper from here, forming a cycle).
// Importing the already-constructed singleton and only reading it at runtime
// inside query() is cycle-safe regardless of evaluation order. It's imported
// via the 'framework' barrel (not './data-provider') so the framework test
// stubs — which map '^framework$' — keep data-provider's JSX out of the jest
// module graph.
import { dataLoading } from 'framework';

dayjs.extend(duration);

const array_indexer = (array, indexer, indexdim, idim = 0) => {
  if (idim == indexdim) {
    const a = [];
    indexer.map((i) => {
      a.push(array[i]);
    });
    return a;
  } else {
    return array.map((v) => array_indexer(v, indexer, indexdim, idim + 1));
  }
};

// Concatenate two arrays along a specific dimension
const array_concat = (arr1, arr2, concatDim, currentDim = 0) => {
  if (currentDim === concatDim) {
    return [...arr1, ...arr2];
  }
  return arr1.map((v, i) =>
    array_concat(v, arr2[i], concatDim, currentDim + 1),
  );
};

// Compute min/max over a 1D or 2D numeric array without spread (avoids
// RangeError: Maximum call stack size exceeded on arrays > ~125k elements).
// NaN values are skipped (comparisons with NaN are always false). This differs
// from the old Math.min(...flat) which propagated NaN — the new behaviour is
// intentionally better: NaN fill-values in datamesh coordinate arrays (e.g.
// masked cells) no longer poison is0to360 / wrapAround detection.
export const arrayMinMax = (arr: number[] | number[][]): [number, number] => {
  let min = Infinity;
  let max = -Infinity;
  if (Array.isArray(arr[0])) {
    for (const row of arr as number[][]) {
      for (const v of row) {
        if (v < min) min = v;
        if (v > max) max = v;
      }
    }
  } else {
    for (const v of arr as number[]) {
      if (v < min) min = v;
      if (v > max) max = v;
    }
  }
  return [min, max];
};

// ---------------------------------------------------------------------------
// EID-108 perf caches
//
// These WeakMaps key on the underlying datamesh Variable object
// (`data.variables[k]`), which is a stable, persistent object reference for a
// Dataset's lifetime — it's a plain-object property lookup, not a freshly
// constructed wrapper on every access. A data refresh always constructs a
// brand-new DataWrapper/Dataset (see worldlayer.ts's cachedQuery comment), so
// entries are naturally invalidated whenever the underlying source actually
// changes, and are garbage collected once the old datasource is no longer
// referenced.
// ---------------------------------------------------------------------------

// P1: string time coordinates are re-parsed with dayjs(t).unix() over the
// entire axis on every query. Numeric coordinates pass through unchanged
// already (no parsing cost), so only the string case needs caching.
const _timeCoordCache = new WeakMap<object, Float64Array>();

export const normalizeTimeCoord = (tvar, tcoordRaw) => {
  if (typeof tcoordRaw[0] !== 'string') return tcoordRaw;
  const cached = _timeCoordCache.get(tvar);
  if (cached) return cached;
  const normalized = Float64Array.from(tcoordRaw, (t) => dayjs(t).unix());
  _timeCoordCache.set(tvar, normalized);
  return normalized;
};

// P5: 'nearest' selection (no groupby) only needs the single closest index —
// sorting the full index array by distance is O(n log n) when a single pass
// suffices. argMinAbsDiff returns the same index the old
// `ind.sort((a,b) => diff[a] < diff[b] ? -1 : 1); ind[0]` selected for every
// input, including ties: both pick the *first* (smallest original index)
// occurrence of the minimum value (verified by fuzz test against the old sort
// in __tests__/argmin-nearest.test.ts). Returns -1 for an empty input,
// matching the old code's `[].slice(0, 1)` on an empty coordinate axis.
export const argMinAbsDiff = (diffs: Float64Array): number => {
  if (diffs.length === 0) return -1;
  let best = 0;
  for (let i = 1; i < diffs.length; i++) {
    if (diffs[i] < diffs[best]) best = i;
  }
  return best;
};

// P6: 2D curvilinear geo filtering re-fetches the x/y coordinate arrays and
// re-scans the full grid for [dataMin, dataMax] (via arrayMinMax, inside
// analyzeLonCoords) on every query, even though both are invariant for the
// life of a datasource — only the bbox-dependent selection
// (iMin/iMax/jMin/jMax) needs to re-run per query, since the bbox itself
// changes on pan/zoom. Cache the fetch + the invariant min/max, keyed on the
// x-coordinate variable.
const _xyCoordCache = new WeakMap<
  object,
  {
    xcoord: unknown;
    ycoord: unknown;
    xdims: string[];
    ydims: string[];
    lonMinMax: [number, number];
  }
>();

export const getXYCoords = async (data, xname, yname) => {
  const xvar = data.variables[xname];
  const cached = _xyCoordCache.get(xvar);
  if (cached) return cached;
  const xcoord = await xvar.get();
  const ycoord = await data.variables[yname].get();
  const result = {
    xcoord,
    ycoord,
    xdims: xvar.dimensions,
    ydims: data.variables[yname].dimensions,
    lonMinMax: arrayMinMax(xcoord),
  };
  _xyCoordCache.set(xvar, result);
  return result;
};

// Detect coordinate system and check if bbox needs wrap-around handling
// Returns { normalizedBbox, wrapAround, lonOffset, coordSystem }
export const analyzeLonCoords = (lonMinMax, bboxX0, bboxX1) => {
  const [dataMin, dataMax] = lonMinMax;

  // Detect coordinate system: 0-360 if min >= 0 and max > 180
  const is0to360 = dataMin >= 0;
  const coordSystem = is0to360 ? '0-360' : '-180-180';

  let x0 = bboxX0;
  let x1 = bboxX1;

  if (is0to360) {
    // Data is 0-360, bbox might be -180-180
    // Convert negative bbox values to 0-360 range
    if (x0 < 0) x0 += 360;
    if (x1 < 0) x1 += 360;
  } else {
    // Data is -180-180, normalize bbox values > 180
    if (x0 > 180) x0 -= 360;
    if (x1 > 180) x1 -= 360;
  }

  // Wrap-around occurs when normalized x0 > x1
  // This means we need to select [x0, max] and [min, x1] and concatenate
  const wrapAround = x0 > x1;

  // Longitude offset to normalize coordinates after extraction
  // For wrap case with 0-360 data: subtract 360 from high values to get continuous range
  // For wrap case with -180-180 data: add 360 to low values
  const lonOffset = wrapAround ? (is0to360 ? -360 : 360) : 0;

  return { x0, x1, wrapAround, lonOffset, coordSystem, dataMin, dataMax };
};

// Build geo selectors for bbox filtering on x/y dimensions
// Supports both 1D coordinate arrays and 2D lon/lat grids
// Handles coordinate system differences (0-360 vs -180-180) and dateline wrap-around
const build_geo_selectors = async (data, geofilter, xname, yname) => {
  if (!geofilter || geofilter.type !== 'bbox' || !xname || !yname) {
    return null;
  }

  const [bboxX0, y0, bboxX1, y1] = geofilter.geom;
  const maxGridSize = geofilter.maxGridSize || null;
  const { xcoord, ycoord, xdims, ydims, lonMinMax } = await getXYCoords(
    data,
    xname,
    yname,
  );

  // Analyze longitude coordinates and normalize bbox
  const { x0, x1, wrapAround, lonOffset, dataMin, dataMax } = analyzeLonCoords(
    lonMinMax,
    bboxX0,
    bboxX1,
  );

  // Check if coordinates are 2D (same dimensions for x and y)
  const is2D =
    xdims.length === 2 &&
    ydims.length === 2 &&
    xdims[0] === ydims[0] &&
    xdims[1] === ydims[1];

  if (is2D) {
    // 2D coordinate arrays - find minimum bounding box indices
    // TODO: Implement wrap-around for 2D grids if needed
    const ny = xcoord.length;
    const nx = xcoord[0].length;
    let iMin = nx,
      iMax = -1,
      jMin = ny,
      jMax = -1;

    for (let j = 0; j < ny; j++) {
      for (let i = 0; i < nx; i++) {
        const x = xcoord[j][i];
        const y = ycoord[j][i];
        const xInBbox = wrapAround ? x >= x0 || x <= x1 : x >= x0 && x <= x1;
        if (xInBbox && y >= y0 && y <= y1) {
          if (i < iMin) iMin = i;
          if (i > iMax) iMax = i;
          if (j < jMin) jMin = j;
          if (j > jMax) jMax = j;
        }
      }
    }

    if (iMax < 0 || jMax < 0) {
      return null;
    }

    // Dimensions are [dim0, dim1] corresponding to [j, i]
    const dim0 = xdims[0];
    const dim1 = xdims[1];

    // Calculate subset step based on bbox-filtered size and maxGridSize
    const bboxNx = iMax - iMin + 1;
    const bboxNy = jMax - jMin + 1;
    const subset = maxGridSize
      ? Math.max(1, Math.ceil(Math.max(bboxNx, bboxNy) / maxGridSize))
      : 1;

    // Apply subset to create subsampled indices
    const jIndices = [];
    for (let j = jMin; j <= jMax; j += subset) jIndices.push(j);
    const iIndices = [];
    for (let i = iMin; i <= iMax; i += subset) iIndices.push(i);

    return {
      selectors: {
        [dim0]: { start: jMin, stop: jMax + 1 },
        [dim1]: { start: iMin, stop: iMax + 1 },
      },
      dimensions: {
        [dim0]: jIndices.length,
        [dim1]: iIndices.length,
      },
      indices:
        subset > 1
          ? {
              [dim0]: jIndices.map((j) => j - jMin),
              [dim1]: iIndices.map((i) => i - iMin),
            }
          : null,
      wrapAround,
      lonOffset,
    };
  } else {
    // 1D coordinate arrays
    const yIndices = [];
    for (let i = 0; i < ycoord.length; i++) {
      if (ycoord[i] >= y0 && ycoord[i] <= y1) yIndices.push(i);
    }

    if (yIndices.length === 0) {
      return null;
    }

    if (wrapAround) {
      // Wrap-around case: select two ranges and concatenate
      // Range 1: x >= x0 (high end, e.g., 330-360)
      // Range 2: x <= x1 (low end, e.g., 0-30)
      const xIndices1 = [];
      const xIndices2 = [];

      for (let i = 0; i < xcoord.length; i++) {
        if (xcoord[i] >= x0 && xcoord[i] <= dataMax) xIndices1.push(i);
        if (xcoord[i] >= dataMin && xcoord[i] <= x1) xIndices2.push(i);
      }

      if (xIndices1.length === 0 && xIndices2.length === 0) {
        return null;
      }

      // Calculate subset step based on total size
      const totalXCount = xIndices1.length + xIndices2.length;
      const subset = maxGridSize
        ? Math.max(
            1,
            Math.ceil(Math.max(totalXCount, yIndices.length) / maxGridSize),
          )
        : 1;

      // Apply subset to both ranges
      const xSubset1 = [];
      for (let i = 0; i < xIndices1.length; i += subset)
        xSubset1.push(xIndices1[i]);
      const xSubset2 = [];
      for (let i = 0; i < xIndices2.length; i += subset)
        xSubset2.push(xIndices2[i]);
      const ySubset = [];
      for (let i = 0; i < yIndices.length; i += subset)
        ySubset.push(yIndices[i]);

      return {
        wrapAround: true,
        lonOffset,
        xname,
        yname,
        // Selectors for first range (high end)
        selectors1: {
          [xname]:
            xIndices1.length > 0
              ? {
                  start: xIndices1[0],
                  stop: xIndices1[xIndices1.length - 1] + 1,
                  step: subset,
                }
              : null,
          [yname]: {
            start: yIndices[0],
            stop: yIndices[yIndices.length - 1] + 1,
            step: subset,
          },
        },
        // Selectors for second range (low end)
        selectors2: {
          [xname]:
            xIndices2.length > 0
              ? {
                  start: xIndices2[0],
                  stop: xIndices2[xIndices2.length - 1] + 1,
                  step: subset,
                }
              : null,
          [yname]: {
            start: yIndices[0],
            stop: yIndices[yIndices.length - 1] + 1,
            step: subset,
          },
        },
        dimensions: {
          [xname]: xSubset1.length + xSubset2.length,
          [yname]: ySubset.length,
        },
        indices: null,
        // Store counts for proper concatenation
        xCount1: xSubset1.length,
        xCount2: xSubset2.length,
      };
    } else {
      // Normal case: single contiguous range
      const xIndices = [];
      for (let i = 0; i < xcoord.length; i++) {
        if (xcoord[i] >= x0 && xcoord[i] <= x1) xIndices.push(i);
      }

      if (xIndices.length === 0) {
        return null;
      }

      // Calculate subset step based on bbox-filtered size and maxGridSize
      const subset = maxGridSize
        ? Math.max(
            1,
            Math.ceil(Math.max(xIndices.length, yIndices.length) / maxGridSize),
          )
        : 1;

      // Apply subset to subsample indices
      const xSubset = [];
      for (let i = 0; i < xIndices.length; i += subset)
        xSubset.push(xIndices[i]);
      const ySubset = [];
      for (let i = 0; i < yIndices.length; i += subset)
        ySubset.push(yIndices[i]);

      return {
        wrapAround: false,
        selectors: {
          [xname]: {
            start: xIndices[0],
            stop: xIndices[xIndices.length - 1] + 1,
            step: subset,
          },
          [yname]: {
            start: yIndices[0],
            stop: yIndices[yIndices.length - 1] + 1,
            step: subset,
          },
        },
        dimensions: {
          [xname]: xSubset.length,
          [yname]: ySubset.length,
        },
        indices: null,
      };
    }
  }
};

// Extract and concatenate data for wrap-around case
const extractWrapAroundData = async (variable, geoResult, levelResult) => {
  const dims = variable.dimensions;
  if (!dims) return variable.get();

  const xname = geoResult.xname;
  const xDimIndex = dims.indexOf(xname);

  // Build selectors for both ranges
  const buildSelector = (selectors) =>
    dims.map((d) => selectors[d] || levelResult?.selector[d] || null);

  // Extract first range (high longitudes)
  let data1 = null;
  if (geoResult.selectors1[xname]) {
    const selector1 = buildSelector(geoResult.selectors1);
    data1 = await variable.get(selector1);
  }

  // Extract second range (low longitudes)
  let data2 = null;
  if (geoResult.selectors2[xname]) {
    const selector2 = buildSelector(geoResult.selectors2);
    data2 = await variable.get(selector2);
  }

  // Concatenate along x dimension
  if (data1 && data2) {
    return array_concat(data1, data2, xDimIndex);
  }
  return data1 || data2;
};

// Normalize longitude coordinates after wrap-around extraction
// For 0-360 data: offset is -360, apply to first part (high values like 330-360 become -30-0)
// For -180/180 data: offset is +360, apply to second part (low values like -180 become 180)
const normalizeLonCoords = (coords, lonOffset, splitIndex) => {
  if (lonOffset < 0) {
    // 0-360 data: apply negative offset to first part (high values)
    return coords.map((v, i) => (i < splitIndex ? v + lonOffset : v));
  } else {
    // -180/180 data: apply positive offset to second part (low/negative values)
    return coords.map((v, i) => (i >= splitIndex ? v + lonOffset : v));
  }
};

// Apply geo selectors to a variable and return filtered data
const apply_geo_filter = async (variable, geoResult) => {
  if (!geoResult) return variable.get();

  const dims = variable.dimensions;
  if (!dims) return variable.get();

  const selector = dims.map((d) => geoResult.selectors[d] || null);
  const hasSelector = selector.some((s) => s !== null);

  if (!hasSelector) return variable.get();

  let data = await variable.get(selector);

  // Apply non-contiguous indexing if needed (1D case)
  if (geoResult.indices) {
    for (const [dimName, indices] of Object.entries(geoResult.indices)) {
      const dind = dims.indexOf(dimName);
      if (dind >= 0) {
        data = array_indexer(data, indices, dind, 0);
      }
    }
  }

  return data;
};

//Squeeze singleton dimensions
// Remove a single size-1 axis at position `axis` from a nested array. axis 0 is
// the outermost; deeper axes recurse so an interior or trailing singleton is
// dropped at the correct level. (Previously squeezing always took data[0],
// which removed the leading axis regardless of where the singleton dimension
// was — corrupting variables whose squeezed dimension, e.g. time for a
// ['station','time'] variable under a nearest filter, was not the first.)
const squeeze_axis = (arr, axis) =>
  axis === 0 ? arr[0] : arr.map((sub) => squeeze_axis(sub, axis - 1));

export const squeeze_dataset = (data) => {
  // The per-record axis — the dimension the geometry/feature variable lives on
  // — must survive even when it has a single record, so feature/label layers
  // can still index value variables per feature (data.variables[v].data[i]).
  // Only filter-reduced axes (time/level) should collapse. Without this, a
  // single-station/single-feature dataset under a nearest-time filter squeezes
  // a ['station','time'] value all the way to a bare scalar — data[i] is then
  // undefined/NaN, so labels render stale and extruded heights collapse, even
  // though the layer is time-resolving correctly. Gridded datasets (coordkeys
  // x/y/t, no g) get recordDim === undefined, so they squeeze as before.
  const recordDim =
    data.coordkeys?.g && data.variables[data.coordkeys.g]?.dimensions?.[0];
  Object.keys(data.variables).forEach((k) => {
    const original = data.variables[k].dimensions;
    if (!original || original.length <= 1) return;
    let dims = [...original];
    let arr = data.variables[k].data;
    // Drop each size-1 dimension at its actual position. Removing an axis
    // shifts the positions of those after it, so resolve the index against the
    // current (shrinking) dims on each iteration.
    original.forEach((d) => {
      if (d === recordDim) return; // never squeeze the record/feature axis
      if (data.dimensions[d] === 1) {
        const axis = dims.indexOf(d);
        if (axis < 0) return;
        arr = squeeze_axis(arr, axis);
        dims = dims.filter((_, i) => i !== axis);
      }
    });
    data.variables[k].data = arr;
    data.variables[k].dimensions = dims;
  });
  return data;
};

export class DataWrapper {
  // EID-131/EID-137: multiscale (GeoZarr pyramid) state. Absent on a flat
  // datasource, which is what keeps every non-multiscale store on exactly its
  // previous code path.
  /** Level-addressable access to the pyramid; set by the zarr open path. */
  multiscale?: import('./multiscale').MultiscaleAccess;
  /** Level identifier currently loaded into `datasource`. */
  activeScale?: number;
  /** Descriptor of that level — carries cellSize and the aggregation operator
   *  the legend discloses when displaying downsampled data. */
  scaleLevel?: import('./multiscale').ScaleLevel;

  constructor(id, datasource, oceanql = {}, refresh = false) {
    this.id = id;
    this.datasource = datasource;
    this.oceanql = oceanql;
    this.refresh = refresh;
  }
  static async inline(id, data) {
    let datasource = null;
    if ((data.features && data.features.length) || data.geometry) {
      datasource = await Dataset.fromGeojson(data);
    } else if (data.variables) {
      const { coordkeys, ..._datasource } = data;
      datasource = await Dataset.init(_datasource, coordkeys);
    }
    return new DataWrapper(id, datasource, {}, true);
  }
  async query(query, squeeze) {
    if (!this.datasource) return null;
    const tname = this.datasource.coordkeys.t;
    const zname = this.datasource.coordkeys.z;
    const xname = this.datasource.coordkeys.x;
    const yname = this.datasource.coordkeys.y;
    const gname = this.datasource.coordkeys.g;
    const qt = tname && query?.timefilter;
    const ql = zname && query?.levelfilter;
    const qg = query?.geofilter;

    dataLoading.start(this.id);
    // EID-110 (M3): the query body has several async awaits (schema/coord
    // fetches, per-variable .get() calls) that can throw. Without this
    // try/finally, a throw left `this.id` in the module-level dataLoading
    // store forever — the loading spinner never cleared and dataLoading
    // .includes(id) kept the id blocked from ever refreshing again.
    try {
      const data = this.datasource;
      const new_variables = {};
      const dimensions = { ...data.dimensions };
      const variables = query?.variables || Object.keys(data.variables);

      // Build geo selectors for bbox filtering (handles wrap-around)
      const geoResult = await build_geo_selectors(data, qg, xname, yname);
      if (qg && qg.type === 'bbox' && !geoResult) {
        return null;
      }
      if (geoResult) {
        Object.assign(dimensions, geoResult.dimensions);
      }

      // Helper to get selectors from geoResult (handles wrap vs non-wrap)
      const getGeoSelector = (dimName) => {
        if (!geoResult) return null;
        if (geoResult.wrapAround && geoResult.selectors1) {
          // For wrap-around, selectors1 is used as primary for non-x dims
          return geoResult.selectors1[dimName] || null;
        }
        return geoResult.selectors?.[dimName] || null;
      };

      // Handle level filter (z coordinate) - build selector like geo
      let levelResult = null;
      if (ql) {
        const zcoord = await data.variables[zname].get();
        let ind = Array.from(zcoord.keys());
        if (ql.type == 'nearest') {
          const z0 = ql.levels[0];
          const zdiff = new Float64Array(zcoord.length);
          zcoord.forEach((z, i) => (zdiff[i] = Math.abs(z - z0)));
          const best = argMinAbsDiff(zdiff);
          ind = best >= 0 ? [best] : [];
          // Apply tolerance if specified
          if (ql.tolerance != null) {
            ind = ind.filter((i) => Math.abs(zcoord[i] - z0) <= ql.tolerance);
          }
        } else if (ql.type == 'range') {
          const z0 = ql.levels[0];
          const z1 = ql.levels[1];
          const indexes = zcoord.map((_, i) => i);
          ind = indexes.filter((i) => z0 <= zcoord[i] && zcoord[i] <= z1);
        }
        if (ind.length == 0) {
          console.warn('No levels found for query', ql);
          return null;
        }
        ind.sort((a, b) => a - b);
        const levelDim = data.variables[zname].dimensions[0];
        const l0 = ind[0];
        const l1 = ind[ind.length - 1] + 1;
        // Check if indices are contiguous
        const isContiguous = ind.length === l1 - l0;
        levelResult = {
          dim: levelDim,
          selector: { [levelDim]: { start: l0, stop: l1 } },
          indices: isContiguous ? null : ind.map((i) => i - l0),
        };
        dimensions[levelDim] = ind.length;
      }

      if (qt) {
        const tcoordRaw = await data.variables[tname].get();
        // Time filtering compares coordinate values arithmetically against the
        // query times (which dayjs().unix() yields as numeric epoch seconds). A
        // dataset whose time variable holds ISO8601 strings (inline/authored
        // sources, transform outputs) would otherwise compare string - number =
        // NaN, dropping every slice. Normalise string coordinates to epoch
        // seconds once; numeric coordinates pass through unchanged. The parsed
        // result is cached per time-variable (EID-108 P1) — the dayjs parse
        // otherwise re-runs over the whole axis on every query.
        const tcoord = normalizeTimeCoord(data.variables[tname], tcoordRaw);
        let ind = Array.from(tcoord.keys());
        if (qt.type == 'nearest') {
          const t0 = dayjs(qt.times[0]).unix();
          const tdiff = new Float64Array(tcoord.length);
          tcoord.forEach((t, i) => (tdiff[i] = Math.abs(t - t0)));
          if (qt.groupby) {
            // groupby needs the full nearest-to-farthest order (first index per
            // group value in that order), so the sort can't be skipped here
            // (EID-108 P5 only replaces the single-nearest, non-groupby case).
            ind.sort((a, b) => (tdiff[a] < tdiff[b] ? -1 : 1));
            const group = await data.variables[qt.groupby].get();
            const groupind = ind.reduce((g, i) => {
              let v = group[i];
              if (!g[v]) g[v] = i;
              return g;
            }, {});
            ind = Object.values(groupind);
          } else {
            const best = argMinAbsDiff(tdiff);
            ind = best >= 0 ? [best] : [];
          }
          const tol =
            (qt.tolerance && dayjs.duration(qt.tolerance).as('seconds')) ||
            tcoord[1] - tcoord[0];
          ind = ind.filter((i) => Math.abs(tcoord[i] - t0) <= tol);
        } else if (qt.type == 'range') {
          // EID-125: null bounds are open-ended (Datamesh `times` semantics) —
          // transform input windows like [null, T] rewrite to a range with a
          // null bound. dayjs(null) is invalid (NaN), which would fail every
          // comparison and null the query out with "No times found".
          const t0 =
            qt.times[0] == null ? -Infinity : dayjs(qt.times[0]).unix();
          const t1 = qt.times[1] == null ? Infinity : dayjs(qt.times[1]).unix();
          const indexes = tcoord.map((_, i) => i);
          ind = indexes.filter((i) => t0 <= tcoord[i] && tcoord[i] <= t1);
        }
        if (ind.length == 0) {
          console.warn('No times found for query', qt);
          return null;
        }
        //Sort by index
        ind.sort((a, b) => a - b);
        const i0 = ind[0];
        const i1 = ind[ind.length - 1] + 1;
        //Sort by time
        ind.sort((a, b) => tcoord[a] - tcoord[b]);
        // The time *dimension* name is not necessarily the time *variable* name
        // (coordkeys.t): feature/record datasets carry a `time` variable on a
        // generic record dimension, and gridded datasets may name the time axis
        // differently. Key the filtered size off the dimension name (as the level
        // filter does) so filtering is correct when time is not the first — or is
        // a differently-named — dimension.
        const tdim = data.variables[tname].dimensions[0];
        // EID-108 P7: the per-variable fetches are independent (each only
        // writes its own new_variables[k]), so run them concurrently instead of
        // sequentially awaiting one variable's get() before starting the next.
        await Promise.all(
          variables.map(async (k) => {
            if (!data.variables[k]) {
              console.warn('Variable ' + k + ' not found');
              return;
            }
            new_variables[k] = {
              dimensions: data.variables[k].dimensions,
              attributes: data.variables[k].attributes,
            };
            if (data.variables[k].dimensions) {
              const dims = data.variables[k].dimensions;
              const tdind = dims.indexOf(tdim);
              let _var;

              // Handle wrap-around case for variables with x dimension
              if (
                geoResult?.wrapAround &&
                geoResult.selectors1 &&
                dims.includes(xname)
              ) {
                // For wrap-around, we need custom extraction
                // Build time selector to apply after wrap extraction
                const timeSelector =
                  tdind >= 0 ? { start: i0, stop: i1 } : null;

                // Extract both ranges and concatenate
                const xDimIndex = dims.indexOf(xname);
                const buildSelector = (selectors) =>
                  dims.map((d, idx) => {
                    if (idx === tdind && timeSelector) return timeSelector;
                    return selectors[d] || levelResult?.selector[d] || null;
                  });

                let data1 = null;
                if (geoResult.selectors1[xname]) {
                  const selector1 = buildSelector(geoResult.selectors1);
                  data1 = await data.variables[k].get(selector1);
                }
                let data2 = null;
                if (geoResult.selectors2[xname]) {
                  const selector2 = buildSelector(geoResult.selectors2);
                  data2 = await data.variables[k].get(selector2);
                }

                _var =
                  data1 && data2
                    ? array_concat(data1, data2, xDimIndex)
                    : data1 || data2;

                // Apply time indexing if needed
                if (tdind >= 0) {
                  const _ind = ind.map((i) => i - i0);
                  _var = array_indexer(_var, _ind, tdind, 0);
                }

                // Normalize longitude coordinates if this is the x coordinate variable
                if (k === xname && geoResult.lonOffset && geoResult.xCount1) {
                  _var = normalizeLonCoords(
                    _var,
                    geoResult.lonOffset,
                    geoResult.xCount1,
                  );
                }
              } else if (tdind < 0) {
                // No time dimension - build selector with geo and level
                const selector = dims.map(
                  (d) => getGeoSelector(d) || levelResult?.selector[d] || null,
                );
                _var = await data.variables[k].get(selector);
                // Apply non-contiguous geo indexing if needed
                if (geoResult?.indices) {
                  for (const [dimName, indices] of Object.entries(
                    geoResult.indices,
                  )) {
                    const dind = dims.indexOf(dimName);
                    if (dind >= 0) _var = array_indexer(_var, indices, dind, 0);
                  }
                }
              } else {
                // Has time dimension - build combined selector with time, geo, and level
                const selector = dims.map(
                  (d) => getGeoSelector(d) || levelResult?.selector[d] || null,
                );
                selector[tdind] = { start: i0, stop: i1 };
                _var = await data.variables[k].get(selector);
                const _ind = ind.map((i) => i - i0);
                _var = array_indexer(_var, _ind, tdind, 0);
                // Apply non-contiguous geo indexing if needed
                if (geoResult?.indices) {
                  for (const [dimName, indices] of Object.entries(
                    geoResult.indices,
                  )) {
                    const dind = dims.indexOf(dimName);
                    if (dind >= 0) _var = array_indexer(_var, indices, dind, 0);
                  }
                }
              }

              // Apply non-contiguous level indexing if needed
              if (levelResult?.indices) {
                const ldind = dims.indexOf(levelResult.dim);
                if (ldind >= 0) {
                  _var = array_indexer(_var, levelResult.indices, ldind, 0);
                }
              }
              new_variables[k].data = _var;
            }
          }),
        );
        dimensions[tdim] = ind.length;
      } else {
        // EID-108 P7: same rationale as the time-filtered branch above — each
        // iteration only writes its own new_variables[k], so the fetches can
        // run concurrently.
        await Promise.all(
          variables.map(async (k) => {
            if (!data.variables[k]) {
              console.warn('Variable ' + k + ' not found');
              return;
            }
            const dims = data.variables[k].dimensions;
            let _var;

            // Handle wrap-around case
            if (
              geoResult?.wrapAround &&
              geoResult.selectors1 &&
              dims?.includes(xname)
            ) {
              _var = await extractWrapAroundData(
                data.variables[k],
                geoResult,
                levelResult,
              );

              // Normalize longitude coordinates if this is the x coordinate variable
              if (k === xname && geoResult.lonOffset && geoResult.xCount1) {
                _var = normalizeLonCoords(
                  _var,
                  geoResult.lonOffset,
                  geoResult.xCount1,
                );
              }
            } else {
              // Normal case: build selector with geo and level
              const selector = dims?.map(
                (d) => getGeoSelector(d) || levelResult?.selector[d] || null,
              );
              const hasSelector = selector?.some((s) => s !== null);
              _var = hasSelector
                ? await data.variables[k].get(selector)
                : await data.variables[k].get();

              // Apply non-contiguous geo indexing if needed
              if (geoResult?.indices && dims) {
                for (const [dimName, indices] of Object.entries(
                  geoResult.indices,
                )) {
                  const dind = dims.indexOf(dimName);
                  if (dind >= 0) _var = array_indexer(_var, indices, dind, 0);
                }
              }
            }

            // Apply non-contiguous level indexing if needed
            if (levelResult?.indices && dims) {
              const ldind = dims.indexOf(levelResult.dim);
              if (ldind >= 0) {
                _var = array_indexer(_var, levelResult.indices, ldind, 0);
              }
            }
            new_variables[k] = {
              dimensions: data.variables[k].dimensions,
              attributes: data.variables[k].attributes,
              data: _var,
            };
          }),
        );
      }
      this.refresh = false;

      // Build result with wrap-around flag if applicable
      const result = {
        attributes: data.attributes,
        variables: new_variables,
        dimensions,
        coordkeys: this.datasource.coordkeys,
        // Flag indicating data was extracted across dateline/meridian wrap
        wrapAround: geoResult?.wrapAround || false,
      };

      if (squeeze) {
        return squeeze_dataset(result);
      } else {
        return result;
      }
    } finally {
      dataLoading.stop(this.id);
    }
  }
}
